import { useMemo } from 'react'
import { mergePrismicPreviewData } from 'gatsby-source-prismic'

import {
  usePrismicPreviewStoreState,
  usePrismicPreviewStoreActions,
} from './usePrismicPreviewStore'

const getMergedData = ({
  shouldShowPreview,
  hasCachedData,
  setCachedPage,
  getCachedDataForPath,
  path,
  data,
  previewData,
}) => {
  // We need to show a preview, but have no cached data. So let's generate it and cache it.
  if (shouldShowPreview && !hasCachedData) {
    const mergedData = mergePrismicPreviewData({
      staticData: data,
      previewData,
    })
    setCachedPage(path, mergedData)

    return mergedData
  }

  // We need to show a preview, but have cached data. Skip the merge and just use our cached merge instead.
  if (shouldShowPreview && hasCachedData) return getCachedDataForPath(path)

  // We don't need to show a preview, so lets just show data straight from gatsby.
  return data
}

export const usePrismicPreviewPageData = ({ data, location }) => {
  const {
    previewsEnabled,
    getPreviewDataForPath,
    hasPreviewDataForPath,
    getCachedDataForPath,
    hasCachedDataForPath,
  } = usePrismicPreviewStoreState()
  const { setCachedPage } = usePrismicPreviewStoreActions()

  const path = useMemo(() => location.pathname, [location.pathname])
  const isPreview = useMemo(() => hasPreviewDataForPath(path), [
    path,
    hasPreviewDataForPath,
  ])
  const hasCachedData = useMemo(() => hasCachedDataForPath(path), [
    path,
    hasCachedDataForPath,
  ])
  const shouldShowPreview = useMemo(() => previewsEnabled && isPreview, [
    previewsEnabled,
    isPreview,
  ])

  const previewData = useMemo(
    () => (shouldShowPreview ? getPreviewDataForPath(path) : data),
    [data, getPreviewDataForPath, path, shouldShowPreview],
  )

  const mergedData = useMemo(
    () =>
      getMergedData({
        shouldShowPreview,
        hasCachedData,
        setCachedPage,
        getCachedDataForPath,
        path,
        data,
        previewData,
      }),
    [
      data,
      getCachedDataForPath,
      hasCachedData,
      path,
      previewData,
      setCachedPage,
      shouldShowPreview,
    ],
  )

  return mergedData
}
